import React from 'react';
import clsx from 'clsx';

export interface RoddoPillTabItem {
  key: string;
  label: string;
}

interface RoddoPillTabsProps {
  items: RoddoPillTabItem[];
  activeKey: string;
  onChange: (key: string) => void;
  className?: string;
  ariaLabel?: string;
}

const RoddoPillTabs: React.FC<RoddoPillTabsProps> = ({
  items,
  activeKey,
  onChange,
  className,
  ariaLabel = 'Roddo section tabs',
}) => {
  const count = Math.max(1, items?.length || 1);
  const isTwoTabRail = count === 2;

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={clsx(
        'roddo-pill-tabs relative overflow-hidden rounded-[12px] border',
        'border-[#DDCDBA] bg-[#F7EFE4]/96 shadow-[inset_0_1px_0_rgba(255,255,255,.72)]',
        'dark:border-[rgba(132,151,190,.17)] dark:bg-[#0C1423]/98 dark:shadow-[inset_0_1px_0_rgba(255,255,255,.025)]',
        isTwoTabRail ? 'mx-auto w-[min(100%,360px)] sm:w-[68%]' : 'w-full',
        className,
      )}
    >
      <div
        className="grid w-full"
        style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}
      >
        {(items ?? []).map((item) => {
          const active = item.key === activeKey;

          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(item.key)}
              className={clsx(
                'roddo-tactile-hit group relative min-h-[44px] px-3 py-2.5 text-center',
                'text-[13px] font-extrabold tracking-[-0.01em] transition-colors duration-180',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#7DB8F4]/45',
                active
                  ? 'text-[#2E6FAD] dark:text-[#DED8FF]'
                  : 'text-[#88786A] hover:text-[#5E5146] dark:text-[#8190AA] dark:hover:text-[#C2CBDC]',
              )}
            >
              <span className="relative z-10 inline-flex flex-col items-center justify-center">
                <span className="leading-5">{item.label}</span>
                <span
                  aria-hidden="true"
                  className={clsx(
                    'mt-1 h-[3px] rounded-full transition-all duration-180',
                    active
                      ? 'w-8 bg-[#65A9EA] dark:bg-[#9278EC]'
                      : 'w-0 bg-transparent',
                  )}
                />
              </span>
            </button>
          );
        })}
      </div>

      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-[#CDBCA9]/55 dark:bg-[#263653]/72"
      />
    </div>
  );
};

export default RoddoPillTabs;
